import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input as UIInput } from '@/components/ui/input';
import { Label as UILabel } from '@/components/ui/label';
import { Database, Search, X, ExternalLink, Car, Phone, LogIn, LogOut, Clock, KeyRound, ChevronRight, Pencil, Save, Upload, Trash2, Loader2, Eye, FileDown, ArrowRightLeft } from 'lucide-react';
import axios from 'axios';
import { getBillTotal } from '@/lib/tenantBilling';
import { isFinanceAccount, currentAccount } from '@/lib/accountPermissions';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DocFileCard = ({ label, url, backendUrl, loading }) => {
  const [missing, setMissing] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const fullUrl = url ? ((url.startsWith('data:') || url.startsWith('http') || url.startsWith('blob:')) ? url : `${backendUrl}${url}`) : null;
  const isPdf = url && !url.startsWith('blob:') && (url.toLowerCase().endsWith('.pdf') || url.startsWith('data:application/pdf'));
  if (!url && loading) return (
    <div className="p-3 rounded-xl border border-gray-200 bg-gray-50 text-center text-xs animate-pulse">
      <p className="text-isk-muted mb-1.5">{label}</p>
      <span className="inline-flex items-center gap-1 text-gray-400"><Loader2 className="w-3 h-3 animate-spin" /> Memuat...</span>
    </div>
  );
  if (!url) return (
    <div className="p-3 rounded-xl border border-red-200 bg-red-50 text-center text-xs">
      <p className="text-isk-muted mb-1.5">{label}</p>
      <span className="text-red-400 font-medium">Tidak ada</span>
    </div>
  );
  if (missing) return (
    <div className="p-3 rounded-xl border border-orange-200 bg-orange-50 text-center text-xs">
      <p className="text-isk-muted mb-1.5">{label}</p>
      <span className="text-orange-500 font-medium">File hilang</span>
      <p className="text-orange-400 mt-0.5">Upload ulang diperlukan</p>
    </div>
  );
  return (
    <>
      {showLightbox && (
        <div className="fixed inset-0 bg-black/90 z-[200] flex flex-col items-center justify-center p-4" onClick={() => setShowLightbox(false)}>
          <button onClick={() => setShowLightbox(false)} className="absolute top-4 right-4 text-white bg-black/40 rounded-full p-2 hover:bg-black/60">
            <X className="w-5 h-5" />
          </button>
          <p className="text-white text-sm font-medium mb-3 opacity-70">{label}</p>
          <img src={fullUrl} alt={label} className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} />
        </div>
      )}
      <div className="p-2 rounded-xl border border-isk-teal bg-isk-teal/5 text-center text-xs">
        <p className="text-isk-muted mb-1.5">{label}</p>
        {isPdf ? (
          <button onClick={() => {
            try {
              const b64 = fullUrl.split(',')[1];
              const bytes = atob(b64);
              const arr = new Uint8Array(bytes.length);
              for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
              window.open(URL.createObjectURL(new Blob([arr], { type: 'application/pdf' })), '_blank');
            } catch { window.open(fullUrl, '_blank'); }
          }} className="inline-flex items-center gap-1 text-isk-teal font-medium hover:underline">
            <ExternalLink className="w-3 h-3" /> Buka PDF
          </button>
        ) : (
          <button onClick={() => setShowLightbox(true)} className="block w-full">
            <img src={fullUrl} alt={label} className="w-full h-20 object-cover rounded-lg mb-1"
              onError={() => setMissing(true)} />
            <span className="inline-flex items-center gap-1 text-isk-teal font-medium hover:underline">
              <ExternalLink className="w-3 h-3" /> Buka penuh
            </span>
          </button>
        )}
      </div>
    </>
  );
};

const EditDocPreview = ({ url, backendUrl }) => {
  const [showLightbox, setShowLightbox] = useState(false);
  const fullUrl = url ? ((url.startsWith('data:') || url.startsWith('http')) ? url : `${backendUrl}${url}`) : null;
  const isPdf = url && (url.toLowerCase().endsWith('.pdf') || url.startsWith('data:application/pdf'));
  return (
    <>
      {showLightbox && (
        <div className="fixed inset-0 bg-black/90 z-[200] flex flex-col items-center justify-center p-4" onClick={() => setShowLightbox(false)}>
          <button onClick={() => setShowLightbox(false)} className="absolute top-4 right-4 text-white bg-black/40 rounded-full p-2 hover:bg-black/60">
            <X className="w-5 h-5" />
          </button>
          <img src={fullUrl} alt="dokumen" className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} />
        </div>
      )}
      {isPdf ? (
        <a href={fullUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-isk-teal hover:underline">
          <ExternalLink className="w-3 h-3" /> Lihat PDF
        </a>
      ) : (
        <button onClick={() => setShowLightbox(true)} className="inline-flex items-center gap-1 text-xs text-isk-teal hover:underline">
          <ExternalLink className="w-3 h-3" /> Lihat saat ini
        </button>
      )}
    </>
  );
};

// Konversi gambar ke JPEG via Canvas agar kompatibel di semua browser (termasuk HEIC dari iPhone)
const toJpeg = (file) => new Promise((resolve) => {
  if (!file.type.startsWith('image/') || file.type === 'application/pdf') { resolve(file); return; }
  const img = new Image();
  const blobUrl = URL.createObjectURL(file);
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext('2d').drawImage(img, 0, 0);
    URL.revokeObjectURL(blobUrl);
    canvas.toBlob((blob) => {
      resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.88);
  };
  img.onerror = () => { URL.revokeObjectURL(blobUrl); resolve(file); };
  img.src = blobUrl;
});

const fmt = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
const fmtCur = (n) => n != null && n !== '' ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n) : '-';

export default function TenantDatabase({ userRole, account }) {
  const isSuperAdmin = userRole === 'superadmin';
  const isFinance = isFinanceAccount(account || currentAccount());
  const canDeleteTenant = isFinance || isSuperAdmin;
  const canViewFormerTenants = isSuperAdmin && !isFinance;
  const canTransferRoom = isFinance || isSuperAdmin;
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [filterProperty, setFilterProperty] = useState('all');
  const [activeFilter, setActiveFilter] = useState('active');
  const [resetResult, setResetResult] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [payments, setPayments] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentImageFailed, setPaymentImageFailed] = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [editFiles, setEditFiles] = useState({ ktp: null, bukti_pembayaran: null, bukti_deposit: null, penghuni2_ktp: null });
  const [deletedDocs, setDeletedDocs] = useState({ ktp: false, bukti_pembayaran: false, bukti_deposit: false, penghuni2_ktp: false });
  const [savePhase, setSavePhase] = useState(null); // null | 'saving' | 'done'
  const [loadingImages, setLoadingImages] = useState(false);
  const [showTransferRoom, setShowTransferRoom] = useState(false);
  const [transferProperties, setTransferProperties] = useState([]);
  const [transferRooms, setTransferRooms] = useState([]);
  const [transferringRoom, setTransferringRoom] = useState(false);
  const [transferForm, setTransferForm] = useState({ target_property_name: '', target_room_number: '', room_price: '' });
  const [agreementPreview, setAgreementPreview] = useState(null);
  const [loadingAgreement, setLoadingAgreement] = useState(false);
  const [agreementError, setAgreementError] = useState('');
  const agreementPreviewUrlRef = useRef(null);

  const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchTenants = () => axios.get(`${BACKEND_URL}/api/admin/tenant-database`, cfg()).then(r => {
    const visible = isFinance ? r.data.filter(t => Number(t.is_active) === 1) : r.data;
    setTenants(visible);
    return visible;
  });

  useEffect(() => {
    fetchTenants().finally(() => setLoading(false));
  }, []);

  useEffect(() => () => {
    if (agreementPreviewUrlRef.current) URL.revokeObjectURL(agreementPreviewUrlRef.current);
  }, []);

  const closeAgreementPreview = () => {
    if (agreementPreviewUrlRef.current) {
      URL.revokeObjectURL(agreementPreviewUrlRef.current);
      agreementPreviewUrlRef.current = null;
    }
    setAgreementPreview(null);
  };

  const openAgreementPreview = async tenant => {
    setAgreementError('');
    setLoadingAgreement(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/tenant-database/${encodeURIComponent(tenant.phone)}/agreement`,
        { ...cfg(), responseType: 'blob' }
      );
      if (agreementPreviewUrlRef.current) URL.revokeObjectURL(agreementPreviewUrlRef.current);
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      agreementPreviewUrlRef.current = url;
      setAgreementPreview({ url, phone: tenant.phone, name: tenant.name });
    } catch (error) {
      setAgreementError(error.response?.data?.detail || 'Perjanjian gagal dimuat');
    } finally {
      setLoadingAgreement(false);
    }
  };

  const downloadAgreement = () => {
    if (!agreementPreview) return;
    const link = document.createElement('a');
    link.href = agreementPreview.url;
    link.download = `Perjanjian-${agreementPreview.phone}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const openDetail = (t) => {
    closeAgreementPreview();
    setAgreementError('');
    setSelected(t);
    setSelectedPayment(null);
    setResetResult(null);
    setPayments([]);
    setLoadingPayments(true);
    setLoadingImages(true);
    axios.get(`${BACKEND_URL}/api/admin/tenant-payments/${t.phone}`, cfg())
      .then(r => setPayments(r.data))
      .catch(() => setPayments([]))
      .finally(() => setLoadingPayments(false));
    axios.get(`${BACKEND_URL}/api/admin/tenants/${t.phone}/images`, cfg())
      .then(r => setSelected(prev => prev?.phone === t.phone ? { ...prev, ...r.data } : prev))
      .catch(() => {})
      .finally(() => setLoadingImages(false));
  };

  const fetchTransferRooms = (propName) => {
    if (!propName) return;
    axios.get(`${BACKEND_URL}/api/staff/rooms`, { params: { property_name: propName }, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => setTransferRooms(r.data.filter(rm => rm.status === 'available').sort((a, b) => String(a.room_number).localeCompare(String(b.room_number), undefined, { numeric: true }))))
      .catch(() => setTransferRooms([]));
  };

  const openEdit = (t) => {
    setEditData({
      name: t.name || '', new_phone: t.phone || '',
      property_name: t.property_name || '', room_number: t.room_number || '',
      checkin_date: t.checkin_date ? t.checkin_date.slice(0,10) : '',
      room_price: t.room_price || '', laundry_price: t.laundry_price || '',
      cleaning_price: t.cleaning_price || '', parkir_price: t.parkir_price || '',
      parkir_flag: !!(t.oe_parkir || parseFloat(t.parkir_price||0) > 0),
      laundry_flag: !!(t.oe_laundry || parseFloat(t.laundry_price||0) > 0),
      cleaning_flag: !!(t.oe_cleaning || parseFloat(t.cleaning_price||0) > 0),
      tempat_lahir: t.tempat_lahir || '', tanggal_lahir: t.tanggal_lahir ? t.tanggal_lahir.slice(0,10) : '',
      pekerjaan: t.pekerjaan || '', alamat_pekerjaan: t.alamat_pekerjaan || '',
      kontak_nama: t.kontak_nama || '', kontak_telp: t.kontak_telp || '', kontak_hubungan: t.kontak_hubungan || '',
      jenis_kendaraan: t.jenis_kendaraan || 'tidak ada', nomor_plat: t.nomor_plat || '',
      deposit_amount: t.deposit?.amount || '', deposit_date: t.deposit?.transaction_date ? t.deposit.transaction_date.slice(0,10) : '',
      deposit_luar_admin: !!t.deposit_luar_admin,
      rekening_bank: t.rekening_bank || '', rekening_nomor: t.rekening_nomor || '', rekening_nama: t.rekening_nama || '',
      laundry_package: t.laundry_package || 'none', laundry_price_per_kg: t.laundry_price_per_kg || '',
      jumlah_penghuni: String(t.jumlah_penghuni || '1'),
      penghuni2_nama: t.penghuni2_nama || '', penghuni2_phone: t.penghuni2_phone || '', penghuni2_status: t.penghuni2_status || '',
    });
    setEditMode(true);
    setEditFiles({ ktp: null, bukti_pembayaran: null, bukti_deposit: null, penghuni2_ktp: null });
    setDeletedDocs({ ktp: false, bukti_pembayaran: false, bukti_deposit: false, penghuni2_ktp: false });
  };

  const openTransferRoom = async (t) => {
    setTransferForm({ target_property_name: t.property_name || '', target_room_number: '', room_price: t.room_price || '' });
    setTransferRooms([]);
    setShowTransferRoom(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/staff/properties`, cfg());
      setTransferProperties(data || []);
      fetchTransferRooms(t.property_name);
    } catch {
      setTransferProperties([]);
    }
  };

  const submitTransferRoom = async (event) => {
    event.preventDefault();
    if (!transferForm.target_property_name || !transferForm.target_room_number || !transferForm.room_price)
      return alert('Lengkapi gedung, kamar tujuan, dan harga sewa baru.');
    setTransferringRoom(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/tenants/${encodeURIComponent(selected.phone)}/transfer-room`, transferForm, cfg());
      setShowTransferRoom(false);
      setEditMode(false);
      const fresh = await fetchTenants();
      const updated = fresh.find(t => String(t.phone) === String(selected.phone));
      setSelected(updated || null);
      alert('Tenant berhasil dipindahkan. Deposit diteruskan tanpa transaksi baru.');
    } catch (error) {
      alert(error.response?.data?.detail || 'Gagal memindahkan tenant');
    } finally {
      setTransferringRoom(false);
    }
  };

  const handleSave = async () => {
    if (editData.parkir_flag && (!editData.parkir_price || parseFloat(editData.parkir_price) === 0))
      return alert('Harga parkir harus diisi jika parkir diaktifkan.');
    if (editData.laundry_flag && (!editData.laundry_price || parseFloat(editData.laundry_price) === 0))
      return alert('Biaya laundry harus diisi jika laundry diaktifkan.');
    if (editData.cleaning_flag && (!editData.cleaning_price || parseFloat(editData.cleaning_price) === 0))
      return alert('Biaya kebersihan harus diisi jika kebersihan diaktifkan.');
    setSavePhase('saving');
    const phone = String(selected.phone);
    try {
      const fd = new FormData();
      Object.keys(editData).forEach(k => {
        if (editData[k] !== undefined) {
          if (k === 'new_phone' && String(editData[k]) === phone) return;
          if (['parkir_flag','laundry_flag','cleaning_flag'].includes(k)) return; // dikirim sebagai parkir/laundry/cleaning
          fd.append(k, editData[k]);
        }
      });
      fd.append('parkir', editData.parkir_flag ? 'true' : 'false');
      fd.append('laundry', editData.laundry_flag ? 'true' : 'false');
      fd.append('cleaning', editData.cleaning_flag ? 'true' : 'false');
      if (editFiles.ktp) fd.append('ktp', editFiles.ktp);
      if (editFiles.bukti_pembayaran) fd.append('bukti_pembayaran', editFiles.bukti_pembayaran);
      if (editFiles.bukti_deposit) fd.append('bukti_deposit', editFiles.bukti_deposit);
      if (editFiles.penghuni2_ktp) fd.append('penghuni2_ktp', editFiles.penghuni2_ktp);
      if (deletedDocs.ktp && !editFiles.ktp) fd.append('delete_ktp', '1');
      if (deletedDocs.bukti_pembayaran && !editFiles.bukti_pembayaran) fd.append('delete_bukti_pembayaran', '1');
      if (deletedDocs.bukti_deposit && !editFiles.bukti_deposit) fd.append('delete_bukti_deposit', '1');
      if (deletedDocs.penghuni2_ktp && !editFiles.penghuni2_ktp) fd.append('delete_penghuni2_ktp', '1');
      await axios.put(`${BACKEND_URL}/api/admin/tenants/${phone}`, fd,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const immediateUpdates = {};
      if (editFiles.ktp) immediateUpdates.ktp_url = URL.createObjectURL(editFiles.ktp);
      else if (deletedDocs.ktp) immediateUpdates.ktp_url = null;
      if (editFiles.bukti_pembayaran) immediateUpdates.bukti_pembayaran_url = URL.createObjectURL(editFiles.bukti_pembayaran);
      else if (deletedDocs.bukti_pembayaran) immediateUpdates.bukti_pembayaran_url = null;
      if (editFiles.bukti_deposit) immediateUpdates.bukti_deposit_url = URL.createObjectURL(editFiles.bukti_deposit);
      else if (deletedDocs.bukti_deposit) immediateUpdates.bukti_deposit_url = null;
      if (editFiles.penghuni2_ktp) immediateUpdates.penghuni2_ktp_url = URL.createObjectURL(editFiles.penghuni2_ktp);
      else if (deletedDocs.penghuni2_ktp) immediateUpdates.penghuni2_ktp_url = null;
      setSelected(prev => ({ ...prev, ...immediateUpdates }));
      setEditMode(false);
      setResetResult(null);
      setSavePhase('done');
    } catch (e) {
      setSavePhase(null);
      alert(e.response?.data?.detail || 'Gagal menyimpan');
      return;
    }
    fetchTenants().then(freshData => {
      const updated = freshData.find(t => String(t.phone) === phone);
      // Hanya update jika modal masih terbuka (user belum tutup detail)
      if (updated) setSelected(prev => prev ? updated : prev);
    }).catch(() => {});
  };

  const properties = [...new Set(tenants.map(t => t.property_name).filter(Boolean))].sort();

  const filtered = tenants.filter(t => {
    const q = search.toLowerCase();
    const matchSearch = !q || t.name?.toLowerCase().includes(q) || t.phone?.includes(q) || t.room_number?.includes(q);
    const matchProp = filterProperty === 'all' || t.property_name === filterProperty;
    const matchActive = !canViewFormerTenants || activeFilter === 'all' || (activeFilter === 'active' ? t.is_active : !t.is_active);
    return matchSearch && matchProp && matchActive;
  });

  const activeCount = isSuperAdmin ? tenants.filter(t => t.is_active).length : tenants.length;

  if (loading) return <div className="p-8 text-center text-isk-muted">Memuat data...</div>;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-isk-text flex items-center gap-2">
            <Database className="w-5 h-5 text-isk-teal" /> Database Tenant
          </h2>
          <p className="text-xs text-isk-muted mt-0.5">
            {canViewFormerTenants ? `${filtered.length} ditampilkan · ${activeCount} aktif · ${tenants.length - activeCount} tidak aktif` : `${tenants.length} tenant aktif`}
          </p>
        </div>
      </div>

      {/* Sub-tabs aktif/non-aktif (superadmin only) */}
      {canViewFormerTenants && (
        <div className="flex gap-1 bg-white/40 rounded-xl p-1 w-fit">
          {[
            { key: 'active', label: `Aktif (${tenants.filter(t => t.is_active).length})` },
            { key: 'inactive', label: `Non Aktif (${tenants.filter(t => !t.is_active).length})` },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => { setActiveFilter(key); setFilterProperty('all'); }}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeFilter === key ? 'bg-white shadow text-isk-text' : 'text-isk-muted hover:text-isk-text'}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Filter + Search */}
      <div className="flex gap-2 flex-wrap">
        {/* Cabang filter */}
        <div className="flex gap-1 bg-white/40 rounded-xl p-1 flex-wrap">
          <button onClick={() => setFilterProperty('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterProperty === 'all' ? 'bg-isk-teal text-white shadow' : 'text-isk-muted hover:text-isk-text'}`}>
            Semua ({tenants.length})
          </button>
          {properties.map(p => {
            const count = tenants.filter(t => t.property_name === p && (isSuperAdmin ? t.is_active : true)).length;
            return (
              <button key={p} onClick={() => setFilterProperty(p)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterProperty === p ? 'bg-isk-teal text-white shadow' : 'text-isk-muted hover:text-isk-text'}`}>
                {p} ({count})
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-isk-muted" />
          <UIInput value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Cari nama, HP, atau nomor kamar..." className="pl-9 rounded-full" />
        </div>
      </div>

      {/* Tenant list */}
      <div className="glass-card rounded-2xl overflow-hidden border border-white/40">
        {/* Header row */}
        <div className="hidden sm:grid grid-cols-[2fr_1fr_1.5fr_1.5fr_1.5fr_1.5fr_auto] gap-3 px-4 py-2.5 bg-white/40 border-b border-white/40 text-xs font-semibold text-isk-muted">
          <span>Nama Tenant</span>
          <span>Kamar</span>
          <span>Cabang</span>
          <span>Tgl Masuk</span>
          <span>Total/Bln</span>
          <span>Deposit</span>
          <span></span>
        </div>

        {filtered.length === 0 && (
          <div className="py-12 text-center text-isk-muted text-sm">Tidak ada data tenant</div>
        )}

        <div className="divide-y divide-white/20">
          {filtered.map((t, i) => (
            <div key={i}
              className={`grid grid-cols-1 sm:grid-cols-[2fr_1fr_1.5fr_1.5fr_1.5fr_1.5fr_auto] gap-2 sm:gap-3 px-4 py-3 items-center transition-colors ${t.deposit_luar_admin ? 'bg-orange-50/60 hover:bg-orange-50' : 'hover:bg-white/30'}`}>
              {/* Nama */}
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-isk-text text-sm">{t.name}</p>
                  {t.deposit_luar_admin ? <span className="px-1.5 py-0.5 bg-orange-100 text-orange-600 rounded text-xs font-medium">Luar Admin</span> : null}
                </div>
                <p className="text-xs text-isk-muted sm:hidden">{t.property_name} · Kamar {t.room_number}</p>
                <div className="flex gap-1 mt-1 flex-wrap">
                  {(parseFloat(t.laundry_price||0) > 0 || (t.laundry_package && t.laundry_package !== 'none')) && (
                    <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[11px] font-medium">Laundry</span>
                  )}
                  {parseFloat(t.cleaning_price||0) > 0 && (
                    <span className="px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded text-[11px] font-medium">Cleaning</span>
                  )}
                  {(parseFloat(t.parkir_price||0) > 0 || (t.jenis_kendaraan && t.jenis_kendaraan !== 'tidak ada')) && (
                    <span className="px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded text-[11px] font-medium">Parkir</span>
                  )}
                  {parseInt(t.jumlah_penghuni) === 2 && (
                    <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded text-[11px] font-medium">2 Penghuni</span>
                  )}
                </div>
              </div>
              {/* Kamar */}
              <p className="hidden sm:block text-sm text-isk-text font-medium">{t.room_number || '-'}</p>
              {/* Cabang */}
              <p className="hidden sm:block text-sm text-isk-text truncate">{t.property_name || '-'}</p>
              {/* Tgl Masuk */}
              <p className="hidden sm:block text-xs text-isk-muted">{fmt(t.checkin_date)}</p>
              {/* Total/Bln */}
              <p className="hidden sm:block text-sm text-isk-teal font-medium">{fmtCur(parseFloat(t.room_price||0) + parseFloat(t.laundry_price||0) + parseFloat(t.cleaning_price||0) + parseFloat(t.parkir_price||0))}</p>
              {/* Deposit */}
              <div className="hidden sm:block">
                {t.deposit ? (
                  <div>
                    <p className="text-sm font-medium text-isk-text">{fmtCur(t.deposit.amount)}</p>
                    <p className="text-xs text-isk-muted">{fmt(t.deposit.transaction_date)}</p>
                  </div>
                ) : <span className="text-xs text-gray-300">-</span>}
              </div>
              {/* Detail button */}
              <Button size="sm" onClick={() => openDetail(t)}
                className="bg-isk-teal/10 hover:bg-isk-teal hover:text-white text-isk-teal rounded-full text-xs h-7 px-3 w-full sm:w-auto transition-colors">
                Detail <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Full-page save overlay */}
      {savePhase && (
        <div className="fixed inset-0 bg-black/70 z-[100] flex flex-col items-center justify-center gap-5">
          {savePhase === 'saving' ? (
            <>
              <div className="w-16 h-16 border-4 border-white/20 border-t-white rounded-full animate-spin" />
              <p className="text-white text-base font-medium tracking-wide">Menyimpan data...</p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full bg-isk-teal flex items-center justify-center shadow-lg">
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-white text-base font-semibold">Data berhasil disimpan</p>
              <button
                onClick={() => setSavePhase(null)}
                className="mt-2 flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white rounded-full px-6 py-2.5 text-sm font-medium transition-colors"
              >
                <X className="w-4 h-4" /> Tutup
              </button>
            </>
          )}
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto relative">
            {/* Modal header */}
            <div className="flex justify-between items-start gap-3 mb-3">
              <div>
                <h3 className="font-bold text-isk-text text-lg">{selected.name}</h3>
                <p className="text-xs text-isk-muted">{selected.phone}</p>
                <div className="flex gap-1 mt-1 flex-wrap">
                  {isSuperAdmin && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${selected.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {selected.is_active ? 'Aktif' : 'Tidak Aktif'}
                    </span>
                  )}
                  {parseInt(selected.jumlah_penghuni) === 2 && (
                    <span className="px-2 py-0.5 bg-indigo-100 text-indigo-600 rounded-full text-xs font-medium">2 Penghuni</span>
                  )}
                  {selected.deposit_luar_admin ? <span className="px-2 py-0.5 bg-orange-100 text-orange-600 rounded-full text-xs font-medium">Deposit Luar Admin</span> : null}
                </div>
              </div>
              <button type="button" aria-label="Tutup detail tenant" onClick={() => { setSelected(null); setResetResult(null); setEditMode(false); }}
                className="p-1.5 -mt-1 -mr-1 rounded-full text-isk-muted hover:bg-gray-100 hover:text-isk-text shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 mb-5" data-testid="tenant-detail-actions">
                {canTransferRoom && Number(selected.is_active) === 1 && !editMode && (
                  <Button size="sm" variant="outline" onClick={() => openTransferRoom(selected)}
                    data-testid="transfer-room-button"
                    className="col-span-2 sm:col-span-1 border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-600 hover:text-white rounded-full text-xs h-9 sm:h-8 transition-colors">
                    <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Pindah Kamar
                  </Button>
                )}
                {!editMode ? (
                  <Button size="sm" onClick={() => openEdit(selected)}
                    className="bg-isk-teal/10 hover:bg-isk-teal hover:text-white text-isk-teal rounded-full text-xs h-8 transition-colors">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                ) : (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setEditMode(false)} className="rounded-full text-xs h-8">Batal</Button>
                    <Button size="sm" onClick={handleSave} disabled={savePhase === 'saving'} className="bg-isk-teal text-white rounded-full text-xs h-8">
                      <Save className="w-3.5 h-3.5 mr-1" /> Simpan
                    </Button>
                  </>
                )}
                {(isSuperAdmin || userRole === 'admin') && !editMode && (
                  <Button size="sm" variant="outline"
                    className="border-orange-300 text-orange-600 hover:bg-orange-50 rounded-full text-xs h-8"
                    disabled={resetting}
                    onClick={async () => {
                      if (!window.confirm(`Reset password ${selected.name}?`)) return;
                      setResetting(true); setResetResult(null);
                      try {
                        const r = await axios.post(`${BACKEND_URL}/api/admin/tenants/${selected.phone}/reset-password`, {}, cfg());
                        setResetResult(r.data.new_password);
                      } catch { setResetResult('ERROR'); }
                      finally { setResetting(false); }
                    }}>
                    <KeyRound className="w-3.5 h-3.5 mr-1" /> {resetting ? 'Mereset...' : 'Reset PW'}
                  </Button>
                )}
                {canDeleteTenant && !editMode && (
                  <Button size="sm" variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-600 hover:text-white rounded-full text-xs h-8"
                    onClick={async () => {
                      if (!window.confirm(`HAPUS PERMANEN semua data tenant "${selected.name}" (${selected.phone})?\n\nTermasuk tagihan, pembayaran, komplain, dan riwayat hunian.\n\nTindakan ini tidak bisa dibatalkan!`)) return;
                      try {
                        await axios.delete(`${BACKEND_URL}/api/admin/tenants/${selected.phone}`, cfg());
                        setSelected(null);
                        fetchTenants();
                      } catch (e) { alert(e.response?.data?.detail || 'Gagal menghapus tenant'); }
                    }}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Hapus Data
                  </Button>
                )}
            </div>

            {resetResult && (
              <div className={`mb-4 p-3 rounded-xl text-sm ${resetResult === 'ERROR' ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-green-50 border border-green-200 text-green-700'}`}>
                {resetResult === 'ERROR' ? 'Gagal mereset password.' : (
                  <>Password baru: <span className="font-mono font-bold text-lg tracking-widest">{resetResult}</span> — catat sebelum menutup.</>
                )}
              </div>
            )}

            {editMode ? (
              <div className="space-y-4">
                <Section title="Data Diri">
                  <div className="grid grid-cols-2 gap-2">
                    {[['Nama Lengkap','name'],['Nomor HP','new_phone'],['Tempat Lahir','tempat_lahir'],['Tanggal Lahir','tanggal_lahir'],['Pekerjaan','pekerjaan'],['Alamat Pekerjaan','alamat_pekerjaan']].map(([label, key]) => (
                      <div key={key}>
                        <UILabel className="text-xs text-isk-muted">{label}</UILabel>
                        <UIInput type={key==='tanggal_lahir'?'date':'text'} value={editData[key]||''} onChange={e => setEditData(d=>({...d,[key]:e.target.value}))} autoComplete={key==='new_phone'?'off':undefined} className="mt-0.5 rounded-lg h-8 text-xs" />
                      </div>
                    ))}
                  </div>
                </Section>
                <Section title="Kontak Darurat">
                  <div className="grid grid-cols-2 gap-2">
                    {[['Nama','kontak_nama'],['Hubungan','kontak_hubungan'],['No. Telp','kontak_telp']].map(([label,key]) => (
                      <div key={key}>
                        <UILabel className="text-xs text-isk-muted">{label}</UILabel>
                        <UIInput value={editData[key]||''} onChange={e => setEditData(d=>({...d,[key]:e.target.value}))} className="mt-0.5 rounded-lg h-8 text-xs" />
                      </div>
                    ))}
                  </div>
                </Section>
                <Section title="Info Hunian & Biaya">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <UILabel className="text-xs text-isk-muted">Properti</UILabel>
                      <UIInput value={editData.property_name||''} disabled className="mt-0.5 rounded-lg h-8 text-xs bg-gray-100" />
                    </div>
                    <div>
                      <UILabel className="text-xs text-isk-muted">Nomor Kamar</UILabel>
                      <UIInput value={editData.room_number||''} disabled className="mt-0.5 rounded-lg h-8 text-xs bg-gray-100" />
                    </div>
                    <div>
                      <UILabel className="text-xs text-isk-muted">Tgl Check-in</UILabel>
                      <UIInput type="date" value={editData.checkin_date||''} onChange={e => setEditData(d=>({...d, checkin_date: e.target.value}))} className="mt-0.5 rounded-lg h-8 text-xs" />
                    </div>
                    <div>
                      <UILabel className="text-xs text-isk-muted">Harga Sewa</UILabel>
                      <UIInput type="number" value={editData.room_price||''} onChange={e => setEditData(d=>({...d, room_price: e.target.value}))} className="mt-0.5 rounded-lg h-8 text-xs" />
                    </div>
                    {[['laundry_flag','laundry_price','Laundry'],['cleaning_flag','cleaning_price','Kebersihan'],['parkir_flag','parkir_price','Parkir']].map(([flagKey,priceKey,label]) => (
                      <div key={priceKey}>
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <input type="checkbox" id={flagKey} checked={!!editData[flagKey]}
                            onChange={e => setEditData(d => ({...d, [flagKey]: e.target.checked, [priceKey]: e.target.checked ? d[priceKey] : ''}))}
                            className="accent-isk-teal w-3.5 h-3.5 cursor-pointer" />
                          <label htmlFor={flagKey} className="text-xs text-isk-muted cursor-pointer select-none">{label}</label>
                        </div>
                        <UIInput type="number" value={editData[priceKey]||''} disabled={!editData[flagKey]}
                          onChange={e => setEditData(d=>({...d,[priceKey]:e.target.value}))}
                          placeholder={editData[flagKey] ? 'Masukkan harga' : 'Tidak aktif'}
                          className={`rounded-lg h-8 text-xs w-full ${!editData[flagKey] ? 'opacity-40 cursor-not-allowed bg-gray-100' : ''}`} />
                      </div>
                    ))}
                  </div>
                </Section>
                <Section title="Deposit">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-isk-muted">Nominal & tanggal</span>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={!!editData.deposit_luar_admin} onChange={e => setEditData(d=>({...d, deposit_luar_admin: e.target.checked, deposit_amount: '', deposit_date: ''}))} className="accent-isk-teal w-3.5 h-3.5" />
                      <span className="text-xs font-medium text-orange-600">Di Luar Admin</span>
                    </label>
                  </div>
                  {editData.deposit_luar_admin ? (
                    <p className="text-xs text-orange-500 italic p-2 rounded-lg bg-orange-50">Deposit dilakukan di luar sistem admin.</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <UILabel className="text-xs text-isk-muted">Jumlah Deposit</UILabel>
                        <UIInput type="number" value={editData.deposit_amount||''} onChange={e => setEditData(d=>({...d,deposit_amount:e.target.value}))} placeholder="0" className="mt-0.5 rounded-lg h-8 text-xs" />
                      </div>
                      <div>
                        <UILabel className="text-xs text-isk-muted">Tanggal Transfer</UILabel>
                        <UIInput type="date" value={editData.deposit_date||''} onChange={e => setEditData(d=>({...d,deposit_date:e.target.value}))} className="mt-0.5 rounded-lg h-8 text-xs" />
                      </div>
                    </div>
                  )}
                </Section>
                <Section title="Kendaraan">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <UILabel className="text-xs text-isk-muted">Jenis Kendaraan</UILabel>
                      <select value={editData.jenis_kendaraan||'tidak ada'} onChange={e => setEditData(d=>({...d,jenis_kendaraan:e.target.value}))} className="mt-0.5 w-full border border-gray-200 rounded-lg px-2 h-8 text-xs bg-white">
                        <option value="tidak ada">Tidak Ada</option>
                        <option value="motor">Motor</option>
                        <option value="mobil">Mobil</option>
                      </select>
                    </div>
                    <div>
                      <UILabel className="text-xs text-isk-muted">Nomor Plat</UILabel>
                      <UIInput value={editData.nomor_plat||''} onChange={e => setEditData(d=>({...d,nomor_plat:e.target.value.toUpperCase()}))} className="mt-0.5 rounded-lg h-8 text-xs" />
                    </div>
                  </div>
                </Section>

                <Section title="Penghuni">
                  <div>
                    <UILabel className="text-xs text-isk-muted">Jumlah Penghuni</UILabel>
                    <select value={editData.jumlah_penghuni||'1'} onChange={e => setEditData(d=>({...d, jumlah_penghuni: e.target.value}))} className="mt-0.5 w-full border border-gray-200 rounded-lg px-2 h-8 text-xs bg-white">
                      <option value="1">1 Orang</option>
                      <option value="2">2 Orang</option>
                    </select>
                  </div>
                  {editData.jumlah_penghuni === '2' && (
                    <div className="mt-3 p-3 rounded-xl bg-indigo-50 border border-indigo-100 space-y-2">
                      <p className="text-xs font-semibold text-indigo-700">Data Penghuni Kedua</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <UILabel className="text-xs text-isk-muted">Nama</UILabel>
                          <UIInput value={editData.penghuni2_nama||''} onChange={e => setEditData(d=>({...d,penghuni2_nama:e.target.value}))} className="mt-0.5 rounded-lg h-8 text-xs" />
                        </div>
                        <div>
                          <UILabel className="text-xs text-isk-muted">No. HP</UILabel>
                          <UIInput value={editData.penghuni2_phone||''} onChange={e => setEditData(d=>({...d,penghuni2_phone:e.target.value}))} className="mt-0.5 rounded-lg h-8 text-xs" />
                        </div>
                        <div className="col-span-2">
                          <UILabel className="text-xs text-isk-muted">Status</UILabel>
                          <select value={editData.penghuni2_status||''} onChange={e => setEditData(d=>({...d,penghuni2_status:e.target.value}))} className="mt-0.5 w-full border border-gray-200 rounded-lg px-2 h-8 text-xs bg-white">
                            <option value="">-- Pilih status --</option>
                            <option value="Pasangan">Pasangan</option>
                            <option value="Saudara">Saudara</option>
                            <option value="Teman">Teman</option>
                            <option value="Lainnya">Lainnya</option>
                          </select>
                        </div>
                      </div>
                      <div className="rounded-xl border border-indigo-200 overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-white">
                          <span className="text-xs font-medium text-isk-text">Foto KTP Penghuni 2</span>
                          {((selected?.penghuni2_ktp_url || selected?.has_penghuni2_ktp_url) && !deletedDocs.penghuni2_ktp) ? (
                            <div className="flex items-center gap-2">
                              {selected?.penghuni2_ktp_url && <EditDocPreview url={selected.penghuni2_ktp_url} backendUrl={BACKEND_URL} />}
                              <button type="button"
                                onClick={() => { setDeletedDocs(d => ({...d, penghuni2_ktp: true})); setEditFiles(f => ({...f, penghuni2_ktp: null})); }}
                                className="p-1 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors" title="Hapus foto">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-red-400">Belum ada</span>
                          )}
                        </div>
                        <label className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-xs transition-colors ${editFiles.penghuni2_ktp ? 'bg-isk-teal/5 text-isk-teal' : 'bg-white text-isk-muted hover:bg-indigo-50'}`}>
                          <Upload className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">
                            {editFiles.penghuni2_ktp ? `✓ ${editFiles.penghuni2_ktp.name}` : `Klik untuk ${((selected?.penghuni2_ktp_url || selected?.has_penghuni2_ktp_url) && !deletedDocs.penghuni2_ktp) ? 'ganti' : 'upload'}`}
                          </span>
                          <input type="file" accept="image/*,.pdf" className="hidden"
                            onChange={async e => {
                              const raw = e.target.files?.[0] || null;
                              const converted = raw ? await toJpeg(raw) : null;
                              setEditFiles(f => ({...f, penghuni2_ktp: converted}));
                              if (converted) setDeletedDocs(d => ({...d, penghuni2_ktp: false}));
                            }} />
                        </label>
                      </div>
                    </div>
                  )}
                </Section>

                <Section title="Rekening Pengembalian Deposit">
                  <div className="grid grid-cols-1 gap-2">
                    {[['Nama Bank', 'rekening_bank'], ['No. Rekening / No. HP', 'rekening_nomor'], ['Nama Pemilik', 'rekening_nama']].map(([label, key]) => (
                      <div key={key}>
                        <UILabel className="text-xs text-isk-muted">{label}</UILabel>
                        <UIInput value={editData[key]||''} onChange={e => setEditData(d=>({...d,[key]:e.target.value}))} className="mt-0.5 rounded-lg h-8 text-xs" />
                      </div>
                    ))}
                  </div>
                </Section>

                <Section title="Paket Laundry">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <UILabel className="text-xs text-isk-muted">Paket</UILabel>
                      <select value={editData.laundry_package||'none'} onChange={e => setEditData(d=>({...d, laundry_package: e.target.value}))} className="mt-0.5 w-full border border-gray-200 rounded-lg px-2 h-8 text-xs bg-white">
                        <option value="none">Kiloan (tidak berlangganan)</option>
                        <option value="14kg">14 kg / Rp100.000</option>
                        <option value="21kg">21 kg / Rp150.000</option>
                      </select>
                    </div>
                    {editData.laundry_package !== 'none' && (
                      <div>
                        <UILabel className="text-xs text-isk-muted">Harga kelebihan (Rp/kg)</UILabel>
                        <UIInput type="number" value={editData.laundry_price_per_kg||''} onChange={e => setEditData(d=>({...d, laundry_price_per_kg: e.target.value}))} placeholder="misal: 7000" className="mt-0.5 rounded-lg h-8 text-xs" />
                      </div>
                    )}
                  </div>
                </Section>

                <Section title="Dokumen">
                  <div className="space-y-2">
                    {[
                      { key: 'ktp', label: 'Foto KTP', currentUrl: selected?.ktp_url, hasDoc: !!(selected?.ktp_url || selected?.has_ktp_url) },
                      { key: 'bukti_pembayaran', label: 'Bukti Pembayaran', currentUrl: selected?.bukti_pembayaran_url, hasDoc: !!(selected?.bukti_pembayaran_url || selected?.has_bukti_pembayaran_url) },
                      { key: 'bukti_deposit', label: 'Bukti Deposit', currentUrl: selected?.bukti_deposit_url, hasDoc: !!(selected?.bukti_deposit_url || selected?.has_bukti_deposit_url) },
                    ].map(({ key, label, currentUrl, hasDoc }) => {
                      const markedDelete = deletedDocs[key];
                      const hasNew = !!editFiles[key];
                      return (
                      <div key={key} className="rounded-xl border border-gray-200 overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
                          <span className="text-xs font-medium text-isk-text">{label}</span>
                          {(hasDoc && !markedDelete) ? (
                            <div className="flex items-center gap-2">
                              {currentUrl && <EditDocPreview url={currentUrl} backendUrl={BACKEND_URL} />}
                              <button type="button"
                                onClick={() => { setDeletedDocs(d => ({...d, [key]: true})); setEditFiles(f => ({...f, [key]: null})); }}
                                className="p-1 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors" title="Hapus foto">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-red-400">Belum ada</span>
                          )}
                        </div>
                        <label className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-xs transition-colors ${hasNew ? 'bg-isk-teal/5 text-isk-teal' : 'bg-white text-isk-muted hover:bg-gray-50'}`}>
                          <Upload className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">
                            {hasNew ? `✓ ${editFiles[key].name}` : `Klik untuk ${(hasDoc && !markedDelete) ? 'ganti' : 'upload'}`}
                          </span>
                          <input type="file" accept="image/*,.pdf" className="hidden"
                            onChange={async e => {
                              const raw = e.target.files?.[0] || null;
                              const converted = raw ? await toJpeg(raw) : null;
                              setEditFiles(f => ({...f, [key]: converted}));
                            }} />
                        </label>
                      </div>
                      );
                    })}
                  </div>
                </Section>
              </div>
            ) : (
            <div className="space-y-4">
              {/* Info Kamar */}
              <Section title="Info Hunian">
                <Grid items={[
                  ['Properti', selected.property_name || '-'],
                  ['Nomor Kamar', selected.room_number || '-'],
                  ['Tgl Check-in', fmt(selected.checkin_date)],
                ]} />
                {/* Rincian biaya */}
                <div className="mt-2 space-y-1">
                  {[
                    ['Sewa Kamar', parseFloat(selected.room_price||0)],
                    parseFloat(selected.laundry_price) > 0 ? ['Laundry', parseFloat(selected.laundry_price)] : null,
                    parseFloat(selected.cleaning_price) > 0 ? ['Kebersihan Kamar', parseFloat(selected.cleaning_price)] : null,
                    parseFloat(selected.parkir_price) > 0 ? ['Parkir', parseFloat(selected.parkir_price)] : null,
                  ].filter(Boolean).map(([label, val]) => (
                    <div key={label} className="flex justify-between items-center text-xs px-2 py-1.5 rounded-lg bg-gray-50">
                      <span className="text-isk-muted">{label}</span>
                      <span className="font-medium text-isk-text">{fmtCur(val)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center text-sm px-2 py-2 rounded-lg bg-isk-teal/10 font-semibold border-t border-isk-teal/20">
                    <span className="text-isk-text">Total/Bulan</span>
                    <span className="text-isk-teal">{fmtCur(parseFloat(selected.room_price||0)+parseFloat(selected.laundry_price||0)+parseFloat(selected.cleaning_price||0)+parseFloat(selected.parkir_price||0))}</span>
                  </div>
                </div>
                {/* Deposit */}
                {selected.deposit ? (
                  <div className="mt-2 p-3 rounded-xl bg-isk-gold/10 border border-isk-gold/20 text-xs">
                    <p className="font-semibold text-isk-text mb-1">Deposit</p>
                    <div className="flex justify-between">
                      <span className="text-isk-muted">Jumlah</span>
                      <span className="font-semibold text-isk-text">{fmtCur(selected.deposit.amount)}</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-isk-muted">Tanggal Transfer</span>
                      <span className="font-medium text-isk-text">{fmt(selected.deposit.transaction_date)}</span>
                    </div>
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-isk-muted italic">Belum ada data deposit.</p>
                )}
              </Section>

              {/* Data Diri */}
              <Section title="Data Diri">
                <Grid items={[
                  ['TTL', `${selected.tempat_lahir || '-'}, ${fmt(selected.tanggal_lahir)}`],
                  ['Pekerjaan', selected.pekerjaan || '-'],
                  ...(isSuperAdmin && selected.tenant_since ? [['Tenant Sejak', fmt(selected.tenant_since)]] : []),
                  ['Jumlah Penghuni', selected.jumlah_penghuni || 1],
                ]} />
              </Section>

              {/* Dokumen */}
              <Section title="Dokumen">
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: 'KTP', url: selected.ktp_url, has: !!selected.has_ktp_url },
                    { label: 'Bukti Pembayaran', url: selected.bukti_pembayaran_url, has: !!selected.has_bukti_pembayaran_url },
                    { label: 'Bukti Deposit', url: selected.bukti_deposit_url, has: !!selected.has_bukti_deposit_url },
                    ...(selected.is_active === 0 ? [{ label: 'Bukti Pengembalian Deposit', url: selected.bukti_pengembalian_deposit_url, has: false }] : []),
                  ].map(({ label, url, has }) => (
                    <DocFileCard key={label} label={label} url={url} backendUrl={BACKEND_URL} loading={loadingImages && has} />
                  ))}
                </div>
              </Section>

              {isFinance && (
                <Section title="Perjanjian Sewa">
                  <div className={`p-3 rounded-xl border ${selected.has_agreement_signed_pdf ? 'border-isk-teal/30 bg-isk-teal/5' : 'border-gray-200 bg-gray-50'}`}>
                    {selected.has_agreement_signed_pdf ? (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs mb-3">
                          <div><p className="text-isk-muted">Ditandatangani</p><p className="font-medium text-isk-text">{fmt(selected.agreement_signed_at)}</p></div>
                          <div><p className="text-isk-muted">Lokasi</p><p className="font-medium text-isk-text">{selected.agreement_location || '-'}</p></div>
                        </div>
                        {agreementError && <p className="mb-2 text-xs text-red-600">{agreementError}</p>}
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => openAgreementPreview(selected)}
                          disabled={loadingAgreement}
                          className="w-full rounded-full border-isk-teal text-isk-teal hover:bg-isk-teal hover:text-white text-xs"
                        >
                          {loadingAgreement ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Eye className="w-4 h-4 mr-1.5" />}
                          {loadingAgreement ? 'Memuat preview...' : 'Preview Perjanjian Bertanda Tangan'}
                        </Button>
                      </>
                    ) : (
                      <p className="text-xs text-isk-muted italic">Perjanjian bertanda tangan belum tersedia.</p>
                    )}
                  </div>
                </Section>
              )}

              {/* Penghuni Kedua */}
              {parseInt(selected.jumlah_penghuni) === 2 && selected.penghuni2_nama && (
                <Section title="Penghuni Kedua">
                  <div className="p-3 rounded-xl bg-indigo-50 border border-indigo-100 space-y-2 text-xs">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="p-2 rounded-lg bg-white/70"><p className="text-isk-muted">Nama</p><p className="font-medium text-isk-text">{selected.penghuni2_nama}</p></div>
                      <div className="p-2 rounded-lg bg-white/70"><p className="text-isk-muted">No. HP</p><p className="font-medium text-isk-text">{selected.penghuni2_phone||'-'}</p></div>
                      <div className="p-2 rounded-lg bg-white/70"><p className="text-isk-muted">Status</p><p className="font-medium text-isk-text">{selected.penghuni2_status||'-'}</p></div>
                    </div>
                    {(selected.penghuni2_ktp_url || (loadingImages && selected.has_penghuni2_ktp_url)) && (
                      <DocFileCard label="KTP Penghuni 2" url={selected.penghuni2_ktp_url} backendUrl={BACKEND_URL} loading={loadingImages && !!selected.has_penghuni2_ktp_url} />
                    )}
                  </div>
                </Section>
              )}

              {/* Kontak Darurat (superadmin) */}
              {isSuperAdmin && selected.kontak_nama && (
                <Section title="Kontak Darurat">
                  <div className="p-3 rounded-xl bg-orange-50 border border-orange-100 text-xs space-y-1">
                    <p className="font-medium text-isk-text">{selected.kontak_nama} <span className="text-isk-muted">({selected.kontak_hubungan})</span></p>
                    <p className="flex items-center gap-1 text-isk-muted"><Phone className="w-3 h-3" />{selected.kontak_telp}</p>
                  </div>
                </Section>
              )}

              {/* Paket Laundry */}
              {selected.laundry_package && selected.laundry_package !== 'none' && (
                <Section title="Paket Laundry">
                  <div className="p-3 rounded-xl bg-blue-50 border border-blue-100 text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-isk-muted">Paket</span>
                      <span className="font-semibold text-blue-700">{selected.laundry_package === '14kg' ? '14 kg / Rp100.000' : '21 kg / Rp150.000'}</span>
                    </div>
                    {selected.laundry_price_per_kg > 0 && (
                      <div className="flex justify-between">
                        <span className="text-isk-muted">Harga kelebihan</span>
                        <span className="font-medium text-isk-text">Rp{Number(selected.laundry_price_per_kg).toLocaleString('id-ID')}/kg</span>
                      </div>
                    )}
                  </div>
                </Section>
              )}

              {/* Rekening Pengembalian Deposit */}
              <Section title="Rekening Pengembalian Deposit">
                {(selected.rekening_bank || selected.rekening_nomor || selected.rekening_nama) ? (
                  <div className="p-3 rounded-xl bg-isk-teal/5 border border-isk-teal/20 text-xs space-y-1">
                    {selected.rekening_bank && (
                      <div className="flex justify-between"><span className="text-isk-muted">Bank</span><span className="font-semibold text-isk-text">{selected.rekening_bank}</span></div>
                    )}
                    {selected.rekening_nomor && (
                      <div className="flex justify-between"><span className="text-isk-muted">No. Rekening</span><span className="font-mono font-semibold text-isk-text">{selected.rekening_nomor}</span></div>
                    )}
                    {selected.rekening_nama && (
                      <div className="flex justify-between"><span className="text-isk-muted">Nama</span><span className="font-semibold text-isk-text">{selected.rekening_nama}</span></div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-isk-muted italic">Belum diisi oleh tenant.</p>
                )}
              </Section>

              {/* Kendaraan */}
              {selected.jenis_kendaraan && selected.jenis_kendaraan !== 'tidak ada' && (
                <Section title="Kendaraan">
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-gray-50 text-xs">
                    <Car className="w-4 h-4 text-isk-muted" />
                    <span className="font-medium capitalize">{selected.jenis_kendaraan}</span>
                    {selected.nomor_plat && <span className="font-mono text-isk-muted">· {selected.nomor_plat}</span>}
                  </div>
                </Section>
              )}

              {/* Riwayat Hunian (superadmin) */}
              {/* Riwayat Pembayaran */}
              <Section title="Riwayat Pembayaran">
                {loadingPayments ? (
                  <p className="text-xs text-isk-muted">Memuat...</p>
                ) : payments.length === 0 ? (
                  <p className="text-xs text-isk-muted italic">Belum ada riwayat pembayaran.</p>
                ) : (
                  <div className="space-y-2">
                    {payments.map((bill, idx) => (
                      <button type="button" key={idx} onClick={() => { setPaymentImageFailed(false); setSelectedPayment(bill); }} className={`w-full text-left flex items-center justify-between p-2.5 rounded-xl border text-xs hover:border-isk-teal ${bill.status === 'paid' ? 'bg-green-50 border-green-100' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-isk-text">{bill.period}</p>
                          <p className="text-isk-muted">{fmtCur(getBillTotal(bill))} · {paymentStatus(bill)}</p>
                          {bill.proof?.submitted_at && <p className="text-isk-muted">{fmt(bill.proof.submitted_at)}</p>}
                        </div>
                        <span className="shrink-0 ml-2 flex items-center gap-1 text-isk-teal">
                          Lihat Detail <ChevronRight className="w-3 h-3" />
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </Section>

              {canViewFormerTenants && selected.occupancy_history?.length > 0 && (
                <Section title="Riwayat Check-in & Check-out">
                  <div className="space-y-2">
                    {selected.occupancy_history.map((ev, idx) => (
                      <div key={idx} className={`flex items-start gap-3 p-2.5 rounded-xl text-xs border ${ev.event_type === 'check_in' ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'}`}>
                        <div className="mt-0.5 shrink-0">
                          {ev.event_type === 'check_in' ? <LogIn className="w-4 h-4 text-green-600" /> : <LogOut className="w-4 h-4 text-red-500" />}
                        </div>
                        <div>
                          <p className={`font-semibold ${ev.event_type === 'check_in' ? 'text-green-700' : 'text-red-600'}`}>
                            {ev.event_type === 'check_in' ? 'Check-in' : 'Check-out'}
                          </p>
                          <p className="text-isk-muted">{ev.property_name} · Kamar {ev.room_number}</p>
                          <p className="flex items-center gap-1 text-isk-muted mt-0.5"><Clock className="w-3 h-3" />{fmt(ev.event_date)}{ev.room_price > 0 ? ` · ${fmtCur(ev.room_price)}/bln` : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </div>
            )}
          </div>
        </div>
      )}
      {showTransferRoom && selected && (
        <div className="fixed inset-0 bg-black/60 z-[120] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Pindah kamar tenant">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="font-bold text-isk-text flex items-center gap-2"><ArrowRightLeft className="w-5 h-5 text-blue-600" /> Pindah Kamar</h3>
                <p className="text-xs text-isk-muted mt-1">{selected.name} · {selected.property_name} / Kamar {selected.room_number}</p>
              </div>
              <button type="button" aria-label="Tutup pindah kamar" onClick={() => setShowTransferRoom(false)}><X className="w-5 h-5 text-isk-muted" /></button>
            </div>
            <form onSubmit={submitTransferRoom} className="space-y-4">
              <div>
                <UILabel className="text-xs text-isk-muted">Gedung Tujuan *</UILabel>
                <select required value={transferForm.target_property_name} onChange={e => {
                  const value = e.target.value;
                  setTransferForm(f => ({ ...f, target_property_name: value, target_room_number: '' }));
                  fetchTransferRooms(value);
                }} className="mt-1 w-full rounded-xl border border-gray-200 px-3 h-10 text-sm bg-white">
                  <option value="">Pilih gedung</option>
                  {transferProperties.map(property => <option key={property.name} value={property.name}>{property.name}</option>)}
                </select>
              </div>
              <div>
                <UILabel className="text-xs text-isk-muted">Kamar Tujuan *</UILabel>
                <select required value={transferForm.target_room_number} onChange={e => setTransferForm(f => ({ ...f, target_room_number: e.target.value }))}
                  className="mt-1 w-full rounded-xl border border-gray-200 px-3 h-10 text-sm bg-white">
                  <option value="">Pilih kamar tersedia</option>
                  {transferRooms.filter(room => !(transferForm.target_property_name === selected.property_name && String(room.room_number) === String(selected.room_number))).map(room => (
                    <option key={room.room_number} value={room.room_number}>Kamar {room.room_number}{room.room_type ? ` · ${room.room_type}` : ''}</option>
                  ))}
                </select>
                {transferRooms.length === 0 && <p className="text-xs text-orange-600 mt-1">Tidak ada kamar tersedia pada gedung ini.</p>}
              </div>
              <div>
                <UILabel className="text-xs text-isk-muted">Harga Sewa Baru *</UILabel>
                <UIInput type="number" min="1" required value={transferForm.room_price} onChange={e => setTransferForm(f => ({ ...f, room_price: e.target.value }))} className="mt-1 rounded-xl" />
                <p className="text-xs text-isk-muted mt-1">Perpindahan langsung diproses hari ini dan tidak dapat dijadwalkan.</p>
              </div>
              <div className="p-3 rounded-xl bg-blue-50 border border-blue-100 text-xs text-blue-700">
                Deposit diteruskan ke kamar baru dengan nominal, tanggal, dan bukti yang sama. Sistem tidak membuat transaksi deposit baru.
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowTransferRoom(false)} disabled={transferringRoom} className="flex-1 rounded-full">Batal</Button>
                <Button type="submit" disabled={transferringRoom || transferRooms.length === 0} className="flex-1 rounded-full bg-blue-600 hover:bg-blue-700 text-white">
                  {transferringRoom ? 'Memindahkan...' : 'Pindahkan Tenant'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {agreementPreview && (
        <div className="fixed inset-0 bg-black/70 z-[220] flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[94vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
              <div className="min-w-0">
                <h3 className="font-bold text-isk-text text-sm sm:text-base">Perjanjian Bertanda Tangan</h3>
                <p className="text-xs text-isk-muted truncate">{agreementPreview.name}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button type="button" onClick={downloadAgreement} className="bg-isk-teal hover:bg-isk-dark text-white rounded-full h-8 px-3 text-xs">
                  <FileDown className="w-4 h-4 mr-1.5" /> Download PDF
                </Button>
                <button type="button" onClick={closeAgreementPreview} aria-label="Tutup preview" className="p-2 rounded-full text-isk-muted hover:bg-gray-100 hover:text-isk-text">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <iframe src={agreementPreview.url} title={`Preview perjanjian ${agreementPreview.name}`} className="w-full flex-1 bg-gray-100 border-0" />
          </div>
        </div>
      )}
      <Dialog open={!!selectedPayment && !!selected} onOpenChange={open => { if (!open) setSelectedPayment(null); }}>
        <DialogContent className="z-[150] w-[calc(100%-2rem)] max-h-[85vh] overflow-y-auto rounded-2xl bg-white">
          <DialogTitle>Detail Pembayaran</DialogTitle>
          <DialogDescription>{selected?.name} · {selectedPayment?.period}</DialogDescription>
          {selectedPayment && <>
            <Grid items={[
              ['Properti', selectedPayment.property_name || selected?.property_name || '-'],
              ['Kamar', selectedPayment.room_number || selected?.room_number || '-'],
              ['Status', paymentStatus(selectedPayment)],
              ['Total Tagihan', fmtCur(getBillTotal(selectedPayment))],
              ['Jatuh Tempo', fmt(selectedPayment.due_date)],
              ['Tanggal Dibayar', fmt(selectedPayment.paid_at)],
              ['Bukti Diunggah', fmt(selectedPayment.proof?.submitted_at)],
            ]} />
            <Section title="Rincian Tagihan">
              <Grid items={[
                ['Sewa Kamar', selectedPayment.rent_amount],
                ['Laundry', selectedPayment.laundry_amount],
                ['Parkir', selectedPayment.parking_amount],
                [selectedPayment.other_description || 'Lainnya', selectedPayment.other_amount],
              ].filter(([, amount]) => Number(amount) > 0).map(([label, amount]) => [label, fmtCur(amount)])} />
            </Section>
            <Section title="Bukti Pembayaran">
              {!selectedPayment.proof?.proof_image_url ? <p className="text-sm text-isk-muted">Belum ada bukti pembayaran.</p>
                : paymentImageFailed ? <p className="text-sm text-red-500">Bukti pembayaran tidak dapat dimuat.</p>
                : /^(data:application\/pdf)|\.pdf(?:[?#]|$)/i.test(selectedPayment.proof.proof_image_url)
                  ? <iframe title="Bukti pembayaran" src={paymentProofUrl(selectedPayment.proof.proof_image_url)} className="w-full h-80 rounded-lg border" />
                  : <img src={paymentProofUrl(selectedPayment.proof.proof_image_url)} alt="Bukti pembayaran" className="w-full max-h-[55vh] object-contain rounded-lg border" onError={() => setPaymentImageFailed(true)} />}
            </Section>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const paymentStatus = bill => bill.status === 'paid' ? 'Lunas' : bill.status === 'cancelled' ? 'Dibatalkan' : bill.payment_status === 'pending_verification' ? 'Menunggu Verifikasi' : 'Belum Lunas';
const paymentProofUrl = url => /^(data:|https?:|blob:)/i.test(url) ? url : `${BACKEND_URL || ''}${url}`;

const Section = ({ title, children }) => (
  <div>
    <p className="text-xs font-semibold text-isk-muted uppercase tracking-wide mb-2">{title}</p>
    {children}
  </div>
);

const Grid = ({ items }) => (
  <div className="grid grid-cols-2 gap-2">
    {items.map(([label, val]) => (
      <div key={label} className="p-2 rounded-lg bg-gray-50 text-xs">
        <p className="text-isk-muted">{label}</p>
        <p className="font-medium text-isk-text">{val}</p>
      </div>
    ))}
  </div>
);
